import type { WasmImports } from "../../maps/Signatures"
import type { FSInstance } from "../../types/WasmInstance"
import { Max, Min } from "@pdfking/math"
import { DefaultMinimap as M } from "../../maps/DefaultMinimap"
import { CreateFSContext } from "../common/CreateFSContext"

// WASI/Emscripten errno values
const ENOENT = 44
const EBADF = 8
const EISDIR = 31

// open flags
const O_CREAT = 64
const O_TRUNC = 512
const O_APPEND = 1024

// seek whence
const SEEK_SET = 0
const SEEK_CUR = 1
const SEEK_END = 2
const EINVAL = 28

type FileEntry = {
  data: Uint8Array
  readonly isDir: boolean
}

type BrowserFD = {
  readonly path: string
  flags: number
  offset: number
}

const normalizePath = (path: string): string => {
  if (path.charAt(0) === "/") {
    const parts = path.split("/").filter(Boolean)
    const result: string[] = []
    for (const part of parts) {
      if (part === "..") result.pop()
      else if (part !== ".") result.push(part)
    }
    return `/${result.join("/")}`
  }
  return path
}

/** Build import functions backed by an in-memory FS. */
export const BuildFSImports = <T extends number | bigint>(
  ctx: FSInstance<T>
) => {
  const files = new Map<string, FileEntry>()
  const fds = new Map<number, BrowserFD>()
  let nextFd = 10

  files.set("/tmp", { data: new Uint8Array(0), isDir: true })
  files.set("/dev", { data: new Uint8Array(0), isDir: true })

  const { utils, common } = CreateFSContext(ctx, fds)
  const {
    heapU8,
    dv,
    getStr,
    readIov,
    writeSize,
    iovStride,
    writeStat,
    getVarargI,
    setVarargs,
  } = utils

  const calculateAt = (dirfd: number, path: string): string => {
    if (path.charAt(0) === "/") return normalizePath(path)
    let dir: string
    if (dirfd === -100) {
      dir = "/"
    } else {
      const stream = fds.get(dirfd)
      if (!stream) return normalizePath(`/${path}`)
      dir = stream.path
    }
    if (path.length === 0) return dir
    return normalizePath(`${dir}/${path}`)
  }

  return {
    ...common,

    [M.__syscall_openat]: (
      dirfd: number,
      pathPtr: number | bigint,
      flags: number,
      va: number | bigint
    ): number => {
      setVarargs(Number(va))
      const mode = va ? getVarargI() : 0
      void mode
      const path = calculateAt(dirfd, getStr(Number(pathPtr)))
      const entry = files.get(path)

      if (!entry && !(flags & O_CREAT)) return -ENOENT
      if (!entry) {
        files.set(path, { data: new Uint8Array(0), isDir: false })
      } else if (entry.isDir) {
        return -EISDIR
      } else if (flags & O_TRUNC) {
        entry.data = new Uint8Array(0)
      }

      const fd = nextFd++
      fds.set(fd, {
        path,
        flags,
        offset: flags & O_APPEND ? (files.get(path)?.data.length ?? 0) : 0,
      })
      return fd
    },

    [M.__syscall_stat64]: (
      pathPtr: number | bigint,
      buf: number | bigint
    ): number => {
      const path = normalizePath(getStr(Number(pathPtr)))
      const entry = files.get(path)
      if (!entry) return -ENOENT
      return writeStat(Number(buf), entry.data.length, entry.isDir)
    },

    [M.__syscall_unlinkat]: (
      dirfd: number,
      pathPtr: number | bigint,
      flags: number
    ): number => {
      const path = calculateAt(dirfd, getStr(Number(pathPtr)))
      const entry = files.get(path)
      if (!entry) return -ENOENT
      if (!flags) {
        if (entry.isDir) return -EISDIR
        files.delete(path)
      } else if (flags === 512) {
        if (!entry.isDir) return -EINVAL
        files.delete(path)
      } else {
        return -EINVAL
      }
      return 0
    },

    [M.__syscall_renameat]: (
      olddirfd: number,
      oldpathPtr: number | bigint,
      newdirfd: number,
      newpathPtr: number | bigint
    ): number => {
      const oldpath = calculateAt(olddirfd, getStr(Number(oldpathPtr)))
      const newpath = calculateAt(newdirfd, getStr(Number(newpathPtr)))
      const entry = files.get(oldpath)
      if (!entry) return -ENOENT
      files.set(newpath, entry)
      files.delete(oldpath)
      for (const [, openFd] of fds) {
        if (openFd.path === oldpath) {
          ;(openFd as { path: string }).path = newpath
        }
      }
      return 0
    },

    [M.__syscall_fstat64]: (fd: number, buf: number | bigint): number => {
      const b = Number(buf)
      if (fd <= 2) return writeStat(b, 0, false)
      const openFd = fds.get(fd)
      if (!openFd) return -EBADF
      const entry = files.get(openFd.path)
      if (!entry) return -EBADF
      return writeStat(b, entry.data.length, entry.isDir)
    },

    [M.fd_close]: (fd: number): number => {
      if (!fds.has(fd)) return EBADF
      fds.delete(fd)
      return 0
    },

    [M.fd_read]: (
      fd: number,
      iov: number | bigint,
      iovcnt: number | bigint,
      pnum: number | bigint
    ): number => {
      const openFd = fds.get(fd)
      if (!openFd) return EBADF
      const entry = files.get(openFd.path)
      if (!entry) return EBADF

      const h8 = heapU8()
      const d = dv()
      let ret = 0
      let iovOff = Number(iov)
      const cnt = Number(iovcnt)

      for (let i = 0; i < cnt; i++) {
        const { ptr, len } = readIov(d, iovOff)
        iovOff += iovStride

        const available = entry.data.length - openFd.offset
        const curr = Min(len, Max(0, available))
        if (curr > 0) {
          h8.set(entry.data.subarray(openFd.offset, openFd.offset + curr), ptr)
          openFd.offset += curr
        }
        ret += curr
        if (curr < len) break
      }

      writeSize(d, Number(pnum), ret)
      return 0
    },

    [M.fd_write]: (
      fd: number,
      iov: number | bigint,
      iovcnt: number | bigint,
      pnum: number | bigint
    ): number => {
      if (fd === 1 || fd === 2) {
        const d = dv()
        let ret = 0
        let iovOff = Number(iov)
        const cnt = Number(iovcnt)
        for (let i = 0; i < cnt; i++) {
          const { len } = readIov(d, iovOff)
          iovOff += iovStride
          ret += len
        }
        writeSize(d, Number(pnum), ret)
        return 0
      }

      const openFd = fds.get(fd)
      if (!openFd) return EBADF
      const entry = files.get(openFd.path)
      if (!entry) return EBADF

      const hU8 = heapU8()
      const d = dv()
      let ret = 0
      let iovOff = Number(iov)
      const cnt = Number(iovcnt)
      for (let i = 0; i < cnt; i++) {
        const { ptr, len } = readIov(d, iovOff)
        iovOff += iovStride

        const needed = openFd.offset + len
        if (needed > entry.data.length) {
          const newData = new Uint8Array(needed)
          newData.set(entry.data)
          entry.data = newData
        }
        entry.data.set(hU8.subarray(ptr, ptr + len), openFd.offset)
        openFd.offset += len
        ret += len
      }

      writeSize(d, Number(pnum), ret)
      return 0
    },

    [M.fd_seek]: (
      fd: number,
      offset: number | bigint,
      whence: number,
      newOffsetPtr: number | bigint
    ): number => {
      const openFd = fds.get(fd)
      if (!openFd) return EBADF
      const entry = files.get(openFd.path)
      if (!entry) return EBADF

      const off = Number(offset)
      if (Number.isNaN(off)) return 61

      let pos: number
      if (whence === SEEK_SET) pos = off
      else if (whence === SEEK_CUR) pos = openFd.offset + off
      else if (whence === SEEK_END) pos = entry.data.length + off
      else return EINVAL

      openFd.offset = pos
      dv().setBigInt64(Number(newOffsetPtr), BigInt(pos), true)
      return 0
    },
  } satisfies Partial<WasmImports<T>>
}
